/**
 * DocumentVersionHistory — displays Egnyte file version history for a contract.
 *
 * Shows a table of document versions with version number, upload date,
 * uploaded by, and file size. Provides "View in Egnyte" and "Download"
 * actions. When no Egnyte file is linked, shows an upload placeholder.
 *
 * Requirements: 4.3, 23.1, 23.2, 23.3
 */

import { useQuery } from '@tanstack/react-query';
import { contractService } from '@/services/contractService';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { formatFileSize, formatDate } from '@/components/documentVersionUtils';
import type { DocumentVersion } from '@/types';

interface DocumentVersionHistoryProps {
  contractId: number;
  egnyteFilePath: string | null;
}

export function DocumentVersionHistory({ contractId, egnyteFilePath }: DocumentVersionHistoryProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['contract-document-versions', contractId],
    queryFn: () => contractService.getDocumentVersions(contractId),
    enabled: !!egnyteFilePath,
  });

  // No document linked — show placeholder
  if (!egnyteFilePath) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          border: '2px dashed #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#f8fafc',
          color: '#94a3b8',
          textAlign: 'center',
        }}
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginBottom: '0.75rem', color: '#cbd5e1' }}
          aria-hidden="true"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="18" x2="12" y2="12" />
          <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
        <p style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', fontWeight: 500, color: '#64748b' }}>
          No Document Linked
        </p>
        <p style={{ margin: 0, fontSize: '0.8rem' }}>
          Upload a contract document to Egnyte to track version history.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading document versions…" />;
  }

  if (error) {
    return (
      <ErrorMessage
        message={(error as Error)?.message ?? 'Failed to load document versions.'}
        onRetry={() => refetch()}
      />
    );
  }

  const versions = data?.versions ?? [];
  const egnyteLink = data?.egnyteLink ?? null;

  return (
    <div>
      {/* Egnyte file link */}
      {egnyteLink && (
        <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <a
            href={egnyteLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              color: '#c8102e',
              textDecoration: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            View in Egnyte
          </a>
        </div>
      )}

      {/* Version history table */}
      {versions.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
          No version history available for this document.
        </p>
      ) : (
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}
          role="table"
          aria-label="Document version history"
        >
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Version</th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Upload Date</th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>Uploaded By</th>
              <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>File Size</th>
              <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((version: DocumentVersion) => (
              <VersionRow
                key={version.entryId}
                version={version}
                contractId={contractId}
                egnyteLink={egnyteLink}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface VersionRowProps {
  version: DocumentVersion;
  contractId: number;
  egnyteLink: string | null;
}

function VersionRow({ version, contractId, egnyteLink }: VersionRowProps) {
  const downloadUrl = contractService.getDocumentDownloadUrl(contractId, version.entryId);

  return (
    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
      <td style={{ padding: '0.4rem 0.5rem' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
          v{version.versionNumber}
          {version.isCurrent && (
            <StatusBadge label="Current" variant="green" />
          )}
        </span>
      </td>
      <td style={{ padding: '0.4rem 0.5rem' }}>
        {formatDate(version.lastModified)}
      </td>
      <td style={{ padding: '0.4rem 0.5rem' }}>
        {version.uploadedBy || '—'}
      </td>
      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
        {formatFileSize(version.size)}
      </td>
      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
        <span style={{ display: 'inline-flex', gap: '0.35rem' }}>
          {egnyteLink && (
            <a
              href={egnyteLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                color: '#c8102e',
                textDecoration: 'none',
                fontSize: '0.75rem',
                cursor: 'pointer',
              }}
              title="View in Egnyte"
            >
              View
            </a>
          )}
          <a
            href={downloadUrl}
            style={{
              padding: '0.2rem 0.5rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
            title={`Download version ${version.versionNumber}`}
          >
            Download
          </a>
        </span>
      </td>
    </tr>
  );
}
